"use client";

import Link from "next/link";
import { useRouter } from "next/navigation";
import { useSession } from "next-auth/react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  CardFooter,
} from "@/components/ui/card";
import { Check, Loader2 } from "lucide-react";
import { useState, useEffect } from "react";
import { SponsoredAdAddOn } from "@/components/home/sponsored-ad-addon";
import { createCheckoutSession } from "@/app/actions/stripe-checkout";
import { toast } from "sonner";

interface Plan {
  id: number;
  title: string;
  price: number;
  interval: string;
  description: string;
  features: string[];
}

interface SponsoredAdData {
  title: string;
  imageBase64: string;
  websiteUrl: string;
}

// Session storage key for persisting sponsored ad data through signup
const PENDING_CHECKOUT_KEY = "usland_pending_checkout";

/**
 * Save pending checkout data to sessionStorage
 */
function savePendingCheckout(
  planType: "PRO" | "ULTIMATE",
  sponsoredAd?: SponsoredAdData,
) {
  if (typeof window === "undefined") return;
  try {
    const data = {
      planType,
      sponsoredAd,
      timestamp: Date.now(),
    };
    sessionStorage.setItem(PENDING_CHECKOUT_KEY, JSON.stringify(data));
  } catch (error) {
    console.error("Failed to save pending checkout:", error);
  }
}

/**
 * Retrieve and clear pending checkout data from sessionStorage
 * Returns null if no pending data or data is stale (> 1 hour old)
 */
export function getPendingCheckout(): {
  planType: "PRO" | "ULTIMATE";
  sponsoredAd?: SponsoredAdData;
} | null {
  if (typeof window === "undefined") return null;
  try {
    const stored = sessionStorage.getItem(PENDING_CHECKOUT_KEY);
    if (!stored) return null;

    // Check if data is stale (older than 1 hour)
    const data = JSON.parse(stored);
    const oneHour = 60 * 60 * 1000;
    if (Date.now() - data.timestamp > oneHour) {
      sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error("Failed to retrieve pending checkout:", error);
    return null;
  }
}

/**
 * Clear pending checkout data from sessionStorage
 */
export function clearPendingCheckout() {
  if (typeof window === "undefined") return;
  try {
    sessionStorage.removeItem(PENDING_CHECKOUT_KEY);
  } catch (error) {
    console.error("Failed to clear pending checkout:", error);
  }
}

interface PricingSectionClientProps {
  plans: Plan[];
}

/**
 * Pricing Section Client Component
 * Handles client-side interactions for pricing plans
 */
export function PricingSectionClient({ plans }: PricingSectionClientProps) {
  const router = useRouter();
  const { data: session } = useSession();
  const [isLoadingCheckout, setIsLoadingCheckout] = useState<string | null>(
    null,
  );

  // Track sponsored ad data for each plan
  const [sponsoredAds, setSponsoredAds] = useState<
    Record<number, SponsoredAdData | null>
  >({});
  const [hasRestoredPendingData, setHasRestoredPendingData] = useState(false);

  // Restore pending checkout data from sessionStorage on mount
  useEffect(() => {
    if (hasRestoredPendingData) return;

    const pending = getPendingCheckout();
    if (pending && session) {
      // Find the plan ID based on plan type
      const plan = plans.find((p) => {
        if (pending.planType === "ULTIMATE")
          return p.title === "Ultimate Unlimited";
        if (pending.planType === "PRO") return p.title === "Pro";
        return false;
      });

      if (plan && pending.sponsoredAd) {
        // Restore the sponsored ad data
        setSponsoredAds(
          (prev) =>
            ({
              ...prev,
              [plan.id]: pending.sponsoredAd,
            }) as Record<number, SponsoredAdData | null>,
        );
        setHasRestoredPendingData(true);

        // Optionally auto-proceed to checkout after a short delay
        // Comment this out if you want the user to manually click "Start Trial"
        /*
        setTimeout(() => {
          handleSubscribeClick(plan.title, plan.id);
        }, 500);
        */
      }
    }
  }, [session, plans, hasRestoredPendingData]);

  const handleFreePlanClick = () => {
    if (session) {
      router.push("/dashboard");
    } else {
      router.push("/auth/signup?callbackUrl=/dashboard");
    }
  };

  const handleSubscribeClick = async (planTitle: string, planId: number) => {
    const planType = planTitle === "Ultimate Unlimited" ? "ULTIMATE" : "PRO";
    const sponsoredAd = sponsoredAds[planId];

    // Check if user is logged in
    if (!session) {
      // Save sponsored ad data to sessionStorage before redirecting to signup
      savePendingCheckout(planType, sponsoredAd || undefined);
      toast.info("Please sign in to complete your subscription");
      router.push("/auth/signup?callbackUrl=/pricing");
      return;
    }

    setIsLoadingCheckout(planTitle);

    try {
      const result = await createCheckoutSession({
        planType,
        sponsoredAd: sponsoredAd || undefined,
      });

      if (result.success && result.checkoutUrl) {
        // Clear pending checkout data since we're proceeding to checkout
        clearPendingCheckout();
        // Redirect to Stripe checkout
        window.location.href = result.checkoutUrl;
      } else {
        toast.error(result.error || "Failed to create checkout session");
      }
    } catch {
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoadingCheckout(null);
    }
  };

  // Handle sponsored ad add-on change
  const handleAdChange = (
    planId: number,
    includeAd: boolean,
    adData?: SponsoredAdData,
  ) => {
    if (includeAd && adData) {
      setSponsoredAds((prev) => ({ ...prev, [planId]: adData }));
    } else {
      setSponsoredAds((prev) => {
        const updated = { ...prev };
        delete updated[planId];
        return updated;
      });
    }
  };

  // Single plan layout - no need for highlighting logic
  const plan = plans[0];

  return (
    <section
      id="pricing"
      className="relative overflow-hidden bg-gradient-to-b from-background via-cyan-950/20 to-background py-16 dark:from-background dark:via-cyan-950/10 dark:to-background md:py-24"
    >
      {/* Decorative background elements */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 left-1/4 h-96 w-96 rounded-full bg-cyan-500/5 blur-3xl dark:bg-cyan-500/10" />
        <div className="absolute -bottom-40 right-1/4 h-96 w-96 rounded-full bg-blue-500/5 blur-3xl dark:bg-blue-500/10" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 text-center">
          <div className="inline-block">
            <h2 className="mb-4 text-3xl font-bold text-primary-foreground text-usland-blue-dark md:text-4xl">
              Simple, Transparent Pricing
            </h2>
            <div className="bg-accent-500 mx-auto h-1 w-24 rounded-full shadow-[0_0_20px_var(--glow-warning-medium)]" />
          </div>
          <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">
            The perfect plan for your real estate investing goals
          </p>
        </div>

        {/* Single centered pricing card */}
        <div className="mx-auto max-w-lg">
          {plan ? (
            (() => {
              const formattedPrice = plan.price === 0 ? "$0" : `$${plan.price}`;
              const period = plan.price > 0 ? `/${plan.interval}` : "";
              const hasSponsoredAd = sponsoredAds[plan.id] !== undefined;
              const adPrice = hasSponsoredAd ? 20 : 0;
              const totalPrice = plan.price + adPrice;

              return (
                <Card className="border-accent-500 flex h-full flex-col bg-primary/80 transition-all duration-300 ease-out hover:scale-105">
                  <CardHeader className="p-6">
                    <CardTitle className="text-2xl font-bold text-usland-white md:text-3xl">
                      {plan.title}
                    </CardTitle>
                    <CardDescription className="text-card-foreground text-white">
                      {plan.description}
                    </CardDescription>
                    <div className="mt-4">
                      <span className="text-accent-500 text-4xl font-bold md:text-5xl">
                        {/*#16191C  */}
                        {formattedPrice}
                      </span>
                      {period && (
                        <span className="text-lg text-muted-foreground">
                          {period}
                        </span>
                      )}
                    </div>
                  </CardHeader>

                  <CardContent className="flex-grow p-6 pt-2">
                    <ul className="space-y-3">
                      {plan.features.map((feature, featureIndex) => (
                        <li
                          key={featureIndex}
                          className="flex items-start gap-3"
                        >
                          <Check className="mt-0.5 h-5 w-5 shrink-0 text-cyan-400" />
                          <span className="break-words text-sm text-usland-white">
                            {feature}
                          </span>
                        </li>
                      ))}
                    </ul>

                    {/* Sponsored Ad Add-on */}
                    {/*<div className="mt-6">*/}
                    {/*  <SponsoredAdAddOn*/}
                    {/*    planPrice={plan.price}*/}
                    {/*    onAddOnChange={(includeAd, adData) => handleAdChange(plan.id, includeAd, adData)}*/}
                    {/*  />*/}
                    {/*</div>*/}
                  </CardContent>

                  <CardFooter className="mt-auto flex flex-col gap-3 p-6 pt-4">
                    <Button
                      onClick={() => handleSubscribeClick(plan.title, plan.id)}
                      disabled={isLoadingCheckout === plan.title}
                      className="from-primary-600 to-accent-500 hover:from-primary-700 hover:to-accent-400 h-[52px] w-full rounded-full bg-gradient-to-r font-bold text-primary-foreground shadow-[0_0_30px_var(--glow-warning-subtle)] transition-all duration-300 hover:scale-105 hover:shadow-[0_0_50px_var(--glow-warning-medium)]"
                    >
                      {isLoadingCheckout === plan.title ? (
                        <>
                          <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                          Processing...
                        </>
                      ) : (
                        "Subscribe Now"
                      )}
                    </Button>
                  </CardFooter>
                </Card>
              );
            })()
          ) : (
            <div className="text-center text-muted-foreground">
              No pricing plan available at this time.
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
